import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import StatusBadge from './StatusBadge';
import KVThumbnails from './KVThumbnails';


const InstallationDetailsModal = ({ 
  isOpen, 
  onClose, 
  installation, 
  onStatusUpdate, 
  onStatusTransition,
  onPhotoUpload,
  onChangeKV
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !installation) return null;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handleStatusClick = () => {
    const currentStatus = installation?.status?.toLowerCase();
    let nextStatus;
    
    switch (currentStatus) {
      case 'planned':
        nextStatus = 'installed';
        break;
      case 'installed':
        nextStatus = 'verified';
        break;
      case 'verified':
        return; // No transition available
      default:
        return;
    }
    
    onStatusTransition?.(installation, nextStatus);
  };

  const handleViewHistory = () => {
    // Switch to audit tab to show history
    setActiveTab('audit');
  };

  const canUploadPhotos = installation?.status?.toLowerCase() === 'installed';
  const isImageRequired = installation?.status?.toLowerCase() === 'installed' && 
                          (!installation?.photos || installation?.photos?.length === 0);
  const isVerified = installation?.status?.toLowerCase() === 'verified';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Installation Details</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {installation?.storeName} - {installation?.brandName}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            {['overview', 'photos', 'timeline', 'audit']?.map((tab) => (
              <button
                key={tab}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
                {tab === 'photos' && installation?.photos?.length > 0 && (
                  <span className="ml-2 bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                    {installation?.photos?.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Status and Actions */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <StatusBadge 
                      status={installation?.status}
                      clickable={true}
                      onClick={handleStatusClick}
                      installation={installation}
                    />
                    {isImageRequired && (
                      <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
                        <Icon name="AlertTriangle" size={14} className="inline mr-1" />
                        Image required for verification
                      </div>
                    )}
                  </div>
                  {canUploadPhotos && (
                    <Button
                      variant="outline"
                      onClick={() => onPhotoUpload?.(installation)}
                    >
                      <Icon name="Camera" size={16} />
                      Upload Photos
                    </Button>
                  )}
                </div>
              </div>

              {/* Installation Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Store Information</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Store Name:</span>
                        <span className="font-medium text-foreground">{installation?.storeName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Store Code:</span>
                        <span className="font-medium text-foreground">{installation?.storeCode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location:</span>
                        <span className="font-medium text-foreground">{installation?.storeLocation}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Branding Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Brand:</span>
                        <span className="font-medium text-foreground">{installation?.brandName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Medium:</span>
                        <span className="font-medium text-foreground">{installation?.brandingType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Branding ID:</span>
                        <span className="font-mono text-sm font-medium text-primary">{installation?.brandingId}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced KV ID Section with conditional logic for Verified status */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-foreground">KV ID</h3>
                      <div className="flex items-center gap-2">
                        {/* Show "View history" link for verified status */}
                        {isVerified && installation?.oldKVId && (
                          <button
                            onClick={handleViewHistory}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            View history
                          </button>
                        )}
                        {/* Change KV button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onChangeKV?.(installation)}
                          className="text-xs h-6 px-2 text-muted-foreground hover:text-primary"
                        >
                          <Icon name="RefreshCw" size={12} />
                          Change KV
                        </Button>
                      </div>
                    </div>
                    <div className="bg-muted/20 rounded-lg p-3">
                      <KVThumbnails installation={installation} size="lg" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Schedule & Assignment</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created On:</span>
                        <span className="font-medium text-foreground">{formatDate(installation?.createdOn)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Installation Date:</span>
                        <span className="font-medium text-foreground">{formatDate(installation?.installationDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Assigned To:</span>
                        <span className="font-medium text-foreground">{installation?.assignedTo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Team Lead:</span>
                        <span className="font-medium text-foreground">{installation?.teamLead}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Cost Information</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Cost:</span>
                        <span className="font-medium text-foreground">{formatCurrency(installation?.totalCost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Quantity:</span>
                        <span className="font-medium text-foreground">{installation?.quantity}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {installation?.notes && (
                <div>
                  <h3 className="font-semibold text-foreground mb-3">Notes</h3>
                  <p className="text-muted-foreground bg-muted/30 rounded-lg p-4">
                    {installation?.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Installation Photos</h3>
                {canUploadPhotos && (
                  <Button
                    variant="outline"
                    onClick={() => onPhotoUpload?.(installation)}
                  >
                    <Icon name="Plus" size={16} />
                    Add Photos
                  </Button>
                )}
              </div>

              {installation?.photos?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {installation?.photos?.map((photo, index) => (
                    <div key={photo?.id || index} className="border border-border rounded-lg overflow-hidden">
                      <div className="aspect-video bg-muted/30">
                        <Image
                          src={photo?.url}
                          alt={photo?.caption || `Installation photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-3">
                        <p className="text-sm text-foreground font-medium">
                          {photo?.caption || `Photo ${index + 1}`}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Uploaded: {formatDate(photo?.uploadedAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Camera" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No photos uploaded</h3>
                  <p className="text-muted-foreground mb-4">
                    {canUploadPhotos 
                      ? 'Upload photos to document the installation progress.' 
                      : 'Photos will be available once the installation is marked as completed.'
                    }
                  </p>
                  {canUploadPhotos && (
                    <Button
                      variant="outline"
                      onClick={() => onPhotoUpload?.(installation)}
                    >
                      <Icon name="Camera" size={16} />
                      Upload Photos
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Installation Timeline</h3>
              <div className="space-y-6">
                {installation?.timeline?.map((event, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={event?.icon} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-foreground">{event?.title}</h4>
                        <span className="text-sm text-muted-foreground">{event?.date}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{event?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground">Audit Log</h3>
              <div className="space-y-4">
                {installation?.auditLog?.map((entry) => (
                  <div key={entry?.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-foreground">{entry?.action}</h4>
                        <p className="text-sm text-muted-foreground">By: {entry?.user}</p>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(entry?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{entry?.details}</p>
                    {entry?.oldValue && entry?.newValue && (
                      <div className="text-xs bg-muted/30 rounded p-2">
                        <span className="text-red-600">From: {entry?.oldValue}</span>
                        <span className="mx-2">→</span>
                        <span className="text-green-600">To: {entry?.newValue}</span>
                        {entry?.imageReference && (
                          <span className="ml-2 text-blue-600">(Image: {entry?.imageReference})</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallationDetailsModal;